import { Link, router } from '@inertiajs/react'
import { useState, useMemo } from 'react'
import { Layout } from '@/components/Layout'
import { PageHead } from '@/components/PageHead'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RefreshCw, CheckCircle, XCircle, Clock, AlertCircle, Search, ArrowUpDown, ArrowUp, ArrowDown, X } from 'lucide-react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'

interface SyncRun {
  id: number
  sync_type: string
  status: string
  started_at: string
  finished_at: string | null
  records_created: number
  records_updated: number
  error_message: string | null
}

interface Client {
  id: string
  name: string
}

interface Instance {
  id: string
  name: string
  client_id: string
  client_name: string
  base_url: string
  sync_status: 'ok' | 'error' | 'never'
  last_workflows_synced_at: string | null
  last_executions_synced_at: string | null
  last_sync_error: string | null
  workflows_count: number
  recent_sync_runs: SyncRun[]
}

interface Props {
  instances: Instance[]
  clients: Client[]
}

type SortKey = 'name' | 'client_name' | 'sync_status' | 'last_synced'
type SortDirection = 'asc' | 'desc'
type StatusFilter = 'all' | 'ok' | 'error' | 'never'

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Never'
  return new Date(dateString).toLocaleString()
}

function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return 'Never'
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}

function SyncStatusBadge({ status }: { status: Instance['sync_status'] }) {
  switch (status) {
    case 'ok':
      return <Badge variant="success"><CheckCircle className="mr-1 h-3 w-3" />Healthy</Badge>
    case 'error':
      return <Badge variant="destructive"><XCircle className="mr-1 h-3 w-3" />Error</Badge>
    case 'never':
    default:
      return <Badge variant="secondary"><Clock className="mr-1 h-3 w-3" />Never synced</Badge>
  }
}

function RunStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'status_success':
      return <Badge variant="success">Success</Badge>
    case 'status_failed':
      return <Badge variant="destructive">Failed</Badge>
    case 'status_pending':
    default:
      return <Badge variant="secondary">Pending</Badge>
  }
}

function getLastSynced(instance: Instance): Date | null {
  const dates = [instance.last_executions_synced_at, instance.last_workflows_synced_at]
    .filter(Boolean)
    .map(d => new Date(d!))
  if (dates.length === 0) return null
  return new Date(Math.max(...dates.map(d => d.getTime())))
}

export default function SyncHealthIndex({ instances, clients }: Props) {
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey | null>('sync_status')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [clientFilter, setClientFilter] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  const handleClientFilterChange = (value: string | null) => {
    setClientFilter(value)
  }

  const clearFilters = () => {
    setClientFilter(null)
    setStatusFilter('all')
    setSearch('')
  }

  const hasActiveFilters = clientFilter || statusFilter !== 'all' || search.trim()

  const handleSync = (instanceId: string, type: 'workflows' | 'executions') => {
    router.post(`/sync_health/${instanceId}/sync`, { type })
  }

  // Filter instances
  const filteredInstances = useMemo(() => {
    let filtered = instances

    // Client filter
    if (clientFilter) {
      filtered = filtered.filter(i => i.client_id === clientFilter)
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(i => i.sync_status === statusFilter)
    }

    // Search filter
    if (search.trim()) {
      const searchLower = search.toLowerCase()
      filtered = filtered.filter(i =>
        i.name.toLowerCase().includes(searchLower) ||
        i.client_name.toLowerCase().includes(searchLower) ||
        i.base_url.toLowerCase().includes(searchLower)
      )
    }

    return filtered
  }, [instances, clientFilter, search, statusFilter])

  // Sort instances
  const sortedInstances = useMemo(() => {
    if (!sortKey) return filteredInstances
    return [...filteredInstances].sort((a, b) => {
      let aVal: string | number | null
      let bVal: string | number | null

      if (sortKey === 'last_synced') {
        const aDate = getLastSynced(a)
        const bDate = getLastSynced(b)
        aVal = aDate ? aDate.getTime() : null
        bVal = bDate ? bDate.getTime() : null
      } else if (sortKey === 'sync_status') {
        const order = { error: 0, never: 1, ok: 2 }
        aVal = order[a.sync_status] ?? 3
        bVal = order[b.sync_status] ?? 3
      } else {
        aVal = a[sortKey]
        bVal = b[sortKey]
      }

      if (aVal == null && bVal == null) return 0
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }
      return sortDirection === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal))
    })
  }, [filteredInstances, sortKey, sortDirection])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
    return sortDirection === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
  }

  const healthyCount = instances.filter(i => i.sync_status === 'ok').length
  const errorCount = instances.filter(i => i.sync_status === 'error').length
  const neverSyncedCount = instances.filter(i => i.sync_status === 'never').length

  return (
    <Layout>
      <PageHead title="Sync Health" description="Monitor sync status of all n8n instances." />
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sync Health</h1>
          <p className="text-muted-foreground">
            Monitor the sync status of all n8n instances.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Healthy</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-success" />
                <span className="text-2xl font-bold">{healthyCount}</span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Errors</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-destructive" />
                <span className="text-2xl font-bold">{errorCount}</span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Never Synced</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <span className="text-2xl font-bold">{neverSyncedCount}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {instances.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center p-12">
              <AlertCircle className="h-12 w-12 text-muted-foreground" />
              <h2 className="mt-4 text-xl font-semibold">No instances configured</h2>
              <p className="mt-2 text-center text-muted-foreground">
                Add n8n instances to start monitoring their sync status.
              </p>
              <Button asChild className="mt-4">
                <Link href="/n8n_instances/new">Add Instance</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Search and Filter Controls */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search instances..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
              <Select
                value={clientFilter || 'all'}
                onValueChange={(value) => handleClientFilterChange(value === 'all' ? null : value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={statusFilter}
                onValueChange={(value) => setStatusFilter(value as StatusFilter)}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="ok">Healthy</SelectItem>
                  <SelectItem value="error">Errors</SelectItem>
                  <SelectItem value="never">Never Synced</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="mr-1 h-4 w-4" />
                  Clear filters
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              Sort by:
              <Button
                variant={sortKey === 'sync_status' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => handleSort('sync_status')}
                className="h-8"
              >
                Status <SortIcon column="sync_status" />
              </Button>
              <Button
                variant={sortKey === 'name' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => handleSort('name')}
                className="h-8"
              >
                Name <SortIcon column="name" />
              </Button>
              <Button
                variant={sortKey === 'client_name' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => handleSort('client_name')}
                className="h-8"
              >
                Client <SortIcon column="client_name" />
              </Button>
              <Button
                variant={sortKey === 'last_synced' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => handleSort('last_synced')}
                className="h-8"
              >
                Last Synced <SortIcon column="last_synced" />
              </Button>
            </div>

            {sortedInstances.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Search className="h-12 w-12 text-muted-foreground" />
                  <h2 className="mt-4 text-xl font-semibold">No instances found</h2>
                  <p className="mt-2 text-center text-muted-foreground">
                    No instances match your search or filter criteria.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Accordion type="single" collapsible className="space-y-4">
                {sortedInstances.map((instance) => (
              <AccordionItem
                key={instance.id}
                value={`instance-${instance.id}`}
                className="rounded-lg border bg-card"
              >
                <AccordionTrigger className="px-4 hover:no-underline">
                  <div className="flex w-full items-center justify-between pr-4">
                    <div className="flex items-center gap-4">
                      <SyncStatusBadge status={instance.sync_status} />
                      <div className="text-left">
                        <p className="font-medium">{instance.name}</p>
                        <p className="text-sm text-muted-foreground">{instance.client_name}</p>
                      </div>
                    </div>
                    <div className="text-right text-sm text-muted-foreground">
                      <p>Workflows: {formatRelativeTime(instance.last_workflows_synced_at)}</p>
                      <p>Executions: {formatRelativeTime(instance.last_executions_synced_at)}</p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  {instance.last_sync_error && (
                    <div className="mb-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                      <strong>Last Error:</strong> {instance.last_sync_error}
                    </div>
                  )}

                  <div className="mb-4 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSync(instance.id, 'workflows')}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Sync Workflows
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSync(instance.id, 'executions')}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Sync Executions
                    </Button>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Workflows</p>
                      <p className="text-lg font-semibold">{instance.workflows_count}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Last Workflow Sync</p>
                      <p className="text-sm">{formatDate(instance.last_workflows_synced_at)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Last Execution Sync</p>
                      <p className="text-sm">{formatDate(instance.last_executions_synced_at)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Base URL</p>
                      <a
                        href={instance.base_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        {instance.base_url}
                      </a>
                    </div>
                  </div>

                  {instance.recent_sync_runs.length > 0 && (
                    <div className="mt-4">
                      <h4 className="mb-2 font-medium">Recent Sync Runs</h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Type</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Started</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead>Updated</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {instance.recent_sync_runs.slice(0, 5).map((run) => (
                            <TableRow key={run.id}>
                              <TableCell className="capitalize">{run.sync_type}</TableCell>
                              <TableCell><RunStatusBadge status={run.status} /></TableCell>
                              <TableCell className="text-sm">{formatDate(run.started_at)}</TableCell>
                              <TableCell>{run.records_created}</TableCell>
                              <TableCell>{run.records_updated}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
              </Accordion>
            )}
          </div>
        )}
      </div>
    </Layout>
  )
}
